import React, { useEffect, useRef } from "react";
import { Brain, Check, ChevronDown, Zap } from "lucide-react";
import ArrowUpIcon from "@src/lib/icons/18px/arrow-up";
import { Button } from "@src/lib/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import { OpusAgentModel } from "@src/types.generated";
import {
  OPUS_AGENT_MODEL_RATING_MAX,
  OPUS_AGENT_MODEL_RATINGS,
  OPUS_AGENT_PICKER_MODELS,
  OpusAgentModelRatingValue,
  opusAgentModelLabel,
} from "./models";

const RATING_STEPS = Array.from(
  { length: OPUS_AGENT_MODEL_RATING_MAX },
  (_, index) => index + 1,
);

// Compact 1–5 meter for a model row: a glyph plus five dots, read out as
// e.g. "Speed 4 of 5". Kept tiny and muted so a row stays one line at the
// drawer's 320px minimum width.
const RatingMeter: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: OpusAgentModelRatingValue;
}> = ({ icon, label, value }) => (
  <span
    role="img"
    aria-label={`${label} ${value} of ${OPUS_AGENT_MODEL_RATING_MAX}`}
    className="flex items-center gap-px"
  >
    <span className="mr-0.5 flex">{icon}</span>
    {RATING_STEPS.map((step) => (
      <span
        key={step}
        className={cn(
          "size-1 rounded-full",
          step <= value ? "bg-icon-secondary" : "bg-border-primary",
        )}
      />
    ))}
  </span>
);

interface OpusAgentComposerProps {
  input: string;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  isRunning: boolean;
  onStop: () => void;
  // The model the next turn runs on (null until the server default is known)
  // and the server default, so the menu can mark it.
  effectiveModel: OpusAgentModel | null;
  defaultModel: OpusAgentModel | null;
  // Allowed any time, mid-run included: it applies to the next turn.
  onSelectModel: (model: OpusAgentModel) => void;
}

// Styling duplicated from the Ask Opus ChatComposer (no attachments / brand
// selector). Unlike Ask Opus, submitting while a turn is RUNNING is allowed:
// the new message supersedes the running turn server-side.
export const OpusAgentComposer: React.FC<OpusAgentComposerProps> = ({
  input,
  onInputChange,
  onSubmit,
  isRunning,
  onStop,
  effectiveModel,
  defaultModel,
  onSelectModel,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const MAX_TEXTAREA_HEIGHT = 240;

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  const isOverflowing = textareaRef.current
    ? textareaRef.current.scrollHeight > MAX_TEXTAREA_HEIGHT
    : false;

  const hasDraft = input.trim().length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (hasDraft) {
      onSubmit();
    }
  };

  return (
    <div className="relative mx-auto flex w-full max-w-2xl flex-col">
      <div className="shadow-dashboard-surface relative z-10 flex flex-col overflow-hidden rounded-xl border border-gray-2 bg-white">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          placeholder="Ask me anything..."
          rows={1}
          autoFocus
          data-testid="chat-composer-input"
          className="max-h-[240px] min-h-[40px] w-full resize-none rounded-none border-none bg-white p-2 text-[14px] leading-[20px] font-normal tracking-[-0.006em] outline-none placeholder:text-muted-foreground focus:ring-0"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
        />
        <div
          className={cn(
            "flex items-end justify-between p-2 transition-all duration-200 ease-in-out",
            isOverflowing && "border-t border-input",
          )}
        >
          <div className="flex flex-1 items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Model"
                  data-testid="opus-agent-model-picker"
                  className="flex h-7 items-center gap-1 rounded-md px-1.5 text-text-secondary transition-colors hover:bg-state-hover focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
                >
                  <Text
                    inline
                    variant="fine-print-title"
                    className="text-text-secondary"
                  >
                    {effectiveModel
                      ? opusAgentModelLabel(effectiveModel)
                      : "Model"}
                  </Text>
                  <ChevronDown className="size-3 shrink-0 text-icon-secondary" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top">
                {OPUS_AGENT_PICKER_MODELS.map((model) => (
                  <DropdownMenuItem
                    key={model}
                    data-testid={`opus-agent-model-option-${model}`}
                    className="justify-between gap-3"
                    onSelect={() => onSelectModel(model)}
                  >
                    <span className="whitespace-nowrap">
                      {opusAgentModelLabel(model)}
                      {model === defaultModel && (
                        <span className="text-text-secondary"> · default</span>
                      )}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <RatingMeter
                        icon={<Zap className="size-3 text-icon-secondary" />}
                        label="Speed"
                        value={OPUS_AGENT_MODEL_RATINGS[model].speed}
                      />
                      <RatingMeter
                        icon={<Brain className="size-3 text-icon-secondary" />}
                        label="Intelligence"
                        value={OPUS_AGENT_MODEL_RATINGS[model].intelligence}
                      />
                      {model === effectiveModel ? (
                        <Check
                          className="size-3.5 shrink-0 text-icon-primary"
                          data-testid="opus-agent-model-check"
                        />
                      ) : (
                        <span className="size-3.5 shrink-0" />
                      )}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {/* While a turn runs the button follows the draft, so pointer and
              keyboard agree: nothing typed → Stop; something typed → Send
              (Enter's action) as the primary, with Stop beside it. */}
          <div className="ml-2 flex items-center gap-1">
            {isRunning && (
              <Button
                type="button"
                variant={hasDraft ? "ghost" : "default"}
                size={hasDraft ? "icon-xs" : "icon"}
                onClick={onStop}
                aria-label="Stop"
                data-testid="opus-agent-stop"
                className={cn(
                  hasDraft
                    ? "text-icon-secondary"
                    : "border border-gray-3 bg-gray-1 text-gray-8 hover:bg-gray-2",
                )}
              >
                <CircleStopIcon />
              </Button>
            )}
            {(!isRunning || hasDraft) && (
              <Button
                size="xs"
                onClick={handleSubmit}
                disabled={!hasDraft}
                aria-label="Send"
                data-testid="opus-agent-send"
                className="size-[28px] rounded-full border border-primary bg-primary p-0 text-white disabled:border-gray-6 disabled:bg-gray-6"
              >
                <ArrowUpIcon className="size-[18px]" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CircleStopIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      width="16"
      height="16"
    >
      <rect width="10" height="10" x="3" y="3" rx="2" />
    </svg>
  );
};
